/**
 * Discord Verification Page
 * Allows external users (alumni, guests, accepted students) to verify for Discord
 */

import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiUser,
  FiMail,
  FiUsers,
  FiCheckCircle,
  FiAlertCircle,
} from "react-icons/fi";
import { toast } from "react-toastify";
import api from "../services/api";

const USER_TYPE_INFO = {
  alumni: {
    label: "Alumni",
    description: "Former RPI students who have graduated",
    emailHint: "Must use @rpi.edu or @alumni.rpi.edu email",
  },
  guest: {
    label: "External Guest",
    description: "Faculty guests, industry partners, or invited visitors",
    emailHint: "Use your non-RPI email address",
  },
  accepted_student: {
    label: "Accepted Student",
    description: "Students who have been accepted to RPI",
    emailHint: "Use any email address",
  },
};

const VerifyDiscord = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get params from URL
  const userId = searchParams.get("userId");
  const guildId = searchParams.get("guildId");
  const userType = searchParams.get("type");
  const token = searchParams.get("token");

  const [formData, setFormData] = useState({
    realName: "",
    email: "",
    affiliation: "",
    cohort: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Validate URL params on mount
  useEffect(() => {
    if (!userId || !guildId || !userType || !token) {
      setError(
        "Invalid verification link. Please request a new link from the Discord bot.",
      );
    } else if (!USER_TYPE_INFO[userType]) {
      setError(
        `Invalid user type: ${userType}. Please request a new link from the Discord bot.`,
      );
    }
  }, [userId, guildId, userType, token]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const payload = {
        discordUserId: userId,
        discordGuildId: guildId,
        userType,
        realName: formData.realName,
        verificationToken: token,
      };

      // Add optional fields if provided
      if (formData.email) {
        payload.email = formData.email;
      }
      if (formData.affiliation) {
        payload.affiliation = formData.affiliation;
      }
      if (formData.cohort && userType === "alumni") {
        payload.cohort = formData.cohort;
      }

      await api.post("/verify/discord", payload);

      setIsSuccess(true);
      toast.success(
        "Verification successful! You should receive your role on Discord shortly.",
      );
    } catch (err) {
      console.error("Verification failed", err);
      const errorMessage =
        err.response?.data?.error || "Verification failed. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show error state if invalid params
  if (error && !formData.realName) {
    return (
      <PageContainer>
        <Container>
          <ErrorCard
            as={motion.div}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <ErrorIcon>
              <FiAlertCircle />
            </ErrorIcon>
            <ErrorTitle>Verification Error</ErrorTitle>
            <ErrorMessage>{error}</ErrorMessage>
            <BackButton onClick={() => navigate("/")}>
              Return to Home
            </BackButton>
          </ErrorCard>
        </Container>
      </PageContainer>
    );
  }

  // Show success state
  if (isSuccess) {
    return (
      <PageContainer>
        <Container>
          <SuccessCard
            as={motion.div}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <SuccessIcon>
              <FiCheckCircle />
            </SuccessIcon>
            <SuccessTitle>Verification Complete!</SuccessTitle>
            <SuccessMessage>
              Your Discord account has been verified. You should receive your{" "}
              {USER_TYPE_INFO[userType]?.label || userType} role shortly.
            </SuccessMessage>
            <SuccessHint>
              You can close this window and return to Discord.
            </SuccessHint>
          </SuccessCard>
        </Container>
      </PageContainer>
    );
  }

  const typeInfo = USER_TYPE_INFO[userType] || {};

  return (
    <PageContainer>
      <Container>
        <HeroSection
          as={motion.div}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Title>Discord Verification</Title>
          <Subtitle>
            Complete your verification to join the ITWS Discord server
          </Subtitle>
        </HeroSection>

        <ContentCard
          as={motion.div}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <TypeBadge>
            <FiUsers />
            <span>{typeInfo.label}</span>
          </TypeBadge>
          <TypeDescription>{typeInfo.description}</TypeDescription>

          <VerifyForm onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="realName">
                <FiUser />
                Full Name *
              </Label>
              <Input
                type="text"
                id="realName"
                name="realName"
                value={formData.realName}
                onChange={handleChange}
                required
                minLength={2}
                maxLength={100}
                placeholder="Your full name"
              />
              <Hint>This will be used to set your Discord nickname</Hint>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="email">
                <FiMail />
                Email {userType === "alumni" && "*"}
              </Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required={userType === "alumni"}
                placeholder="your.email@example.com"
              />
              <Hint>{typeInfo.emailHint}</Hint>
            </FormGroup>

            {userType === "alumni" && (
              <FormGroup>
                <Label htmlFor="cohort">Graduation Year</Label>
                <Input
                  type="text"
                  id="cohort"
                  name="cohort"
                  value={formData.cohort}
                  onChange={handleChange}
                  placeholder="e.g., 2020"
                  pattern="\d{4}"
                  maxLength={4}
                />
                <Hint>Optional: Your graduation year</Hint>
              </FormGroup>
            )}

            {userType === "guest" && (
              <FormGroup>
                <Label htmlFor="affiliation">Affiliation</Label>
                <Input
                  type="text"
                  id="affiliation"
                  name="affiliation"
                  value={formData.affiliation}
                  onChange={handleChange}
                  placeholder="e.g., Company Name or University"
                />
                <Hint>Optional: Your organization or affiliation</Hint>
              </FormGroup>
            )}

            {error && (
              <ErrorBanner>
                <FiAlertCircle />
                {error}
              </ErrorBanner>
            )}

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Verifying..." : "Complete Verification"}
            </SubmitButton>
          </VerifyForm>
        </ContentCard>
      </Container>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  padding: 5rem 0;
  display: flex;
  align-items: center;
`;

const Container = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 0 2rem;
  width: 100%;
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 1.125rem;
  color: var(--text-secondary);
`;

const ContentCard = styled.div`
  background: var(--bg-secondary);
  padding: 2.5rem;
  border-radius: 1rem;
  box-shadow: var(--shadow-lg);
`;

const TypeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--accent-gradient);
  color: white;
  border-radius: 2rem;
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.75rem;

  svg {
    font-size: 1rem;
  }
`;

const TypeDescription = styled.p`
  color: var(--text-secondary);
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--border-color);
`;

const VerifyForm = styled.form``;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--text-primary);

  svg {
    color: var(--accent-primary);
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  background: var(--bg-primary);
  border: 2px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-primary);
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Hint = styled.p`
  font-size: 0.8rem;
  color: var(--text-tertiary);
  margin-top: 0.375rem;
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 0.5rem;
  color: #ef4444;
  margin-bottom: 1.5rem;

  svg {
    flex-shrink: 0;
    font-size: 1.25rem;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem 2rem;
  background: var(--accent-gradient);
  color: white;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessCard = styled.div`
  background: var(--bg-secondary);
  padding: 3rem;
  border-radius: 1rem;
  box-shadow: var(--shadow-lg);
  text-align: center;
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  border-radius: 50%;
  background: rgba(34, 197, 94, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #22c55e;
  font-size: 2.5rem;
`;

const SuccessTitle = styled.h2`
  margin-bottom: 1rem;
  color: #22c55e;
`;

const SuccessMessage = styled.p`
  color: var(--text-secondary);
  margin-bottom: 1rem;
  line-height: 1.6;
`;

const SuccessHint = styled.p`
  font-size: 0.875rem;
  color: var(--text-tertiary);
`;

const ErrorCard = styled.div`
  background: var(--bg-secondary);
  padding: 3rem;
  border-radius: 1rem;
  box-shadow: var(--shadow-lg);
  text-align: center;
`;

const ErrorIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ef4444;
  font-size: 2.5rem;
`;

const ErrorTitle = styled.h2`
  margin-bottom: 1rem;
  color: #ef4444;
`;

const ErrorMessage = styled.p`
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const BackButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: var(--bg-primary);
  border: 2px solid var(--border-color);
  color: var(--text-primary);
  border-radius: 0.5rem;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--accent-primary);
    color: var(--accent-primary);
  }
`;

export default VerifyDiscord;
